import React, { useState, useEffect } from 'react';
import './AdminCategories.css';
import categoriesService from '../../../services/categories.service';
import { useToast } from '../../../context/ToastContext';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [fixedCategories, setFixedCategories] = useState([]);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
    icon: ''
  });
    const toast = useToast();


  // Fetch categories on mount
  useEffect(() => {
    initializeAndFetchCategories();
  }, []);

  const initializeAndFetchCategories = async () => {
    setLoading(true);
    try {
      // Then fetch all categories
      await fetchCategories();
    } catch (error) {
      console.error('Error initializing categories:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const result = await categoriesService.getAllCategories();
      if (result.success) {
        setCategories(result.categories);
        setFixedCategories(result.categories.filter(cat => cat.isFixed));
        setDynamicCategories(result.categories.filter(cat => !cat.isFixed));
      } else {
        console.error('Error fetching categories:', result.error);
        toast.showError('Erreur lors du chargement des catégories: ' + result.error);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.showError('Une erreur est survenue lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Open modal for adding
  const handleAdd = () => {
    setModalMode('add');
    setFormData({
      name: '',
      description: '',
      color: '#3b82f6',
      icon: ''
    });
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (category) => {
    setModalMode('edit');
    setSelectedCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      color: category.color || '#3b82f6',
      icon: category.icon || ''
    });
    setShowModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let result;
      if (modalMode === 'add') {
        result = await categoriesService.addCategory(formData);
      } else {
        result = await categoriesService.updateCategory(selectedCategory.id, formData);
      }

      if (result.success) {
        await fetchCategories();
        setShowModal(false);
        toast.showSuccess(result.message || `Catégorie ${modalMode === 'add' ? 'ajoutée' : 'modifiée'} avec succès!`);
      } else {
        toast.showError('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Error submitting category:', error);
      toast.showError('Une erreur est survenue: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete category
  const handleDelete = async (categoryId) => {
    if (!deleteConfirm) {
      setDeleteConfirm(categoryId);
      setTimeout(() => setDeleteConfirm(null), 3000); // Auto-cancel after 3 seconds
      return;
    }

    setLoading(true);
    try {
      const result = await categoriesService.deleteCategory(categoryId);
      if (result.success) {
        await fetchCategories();
        toast.showSuccess(result.message || 'Catégorie supprimée avec succès!');
      } else {
        toast.showError('Erreur: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.showError('Une erreur est survenue: ' + error.message);
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  // Filter dynamic categories only
  const filteredDynamicCategories = dynamicCategories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get category initials for icon
  const getCategoryInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <div className="admin-category-admin-categories">
      {/* Fixed Categories Section */}
      <div className="admin-category-categories-section">
        <div className="admin-category-section-header-admin">
          <h2>Catégories Principales</h2>
          <span className="admin-category-section-badge">Non supprimables</span>
                  <button className="admin-category-cat-btn admin-category-cat-btn-primary" onClick={handleAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Ajouter une catégorie
        </button>
        </div>
        
        {loading ? (
          <div className="admin-category-loading-state">
            <div className="admin-category-spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : (
          <div className="admin-category-categories-grid">
            {fixedCategories.map(category => (
              <div key={category.id} className="admin-category-category-card admin-category-category-card-fixed">
                <div className="admin-category-category-card-header">
                  <div 
                    className="admin-category-category-icon" 
                    style={{ background: category.color || '#3b82f6' }}
                  >
                    {category.icon || getCategoryInitials(category.name)}
                  </div>
                  <div className="admin-category-category-actions">
                    <button
                      className="admin-category-cat-btn-icon admin-category-cat-btn-edit"
                      onClick={() => handleEdit(category)}
                      title="Modifier"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className="admin-category-cat-btn-icon admin-category-cat-btn-delete admin-category-cat-btn-delete-disabled"
                      disabled
                      title="Les catégories principales ne peuvent pas être supprimées"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="admin-category-category-info">
                  <h3>{category.name}</h3>
                  <p>{category.description || 'Aucune description'}</p>
                </div>
                <div className="admin-category-category-stats">
                  <div className="admin-category-stat-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    </svg>
                    <span><span className="admin-category-stat-value">{category.productCount || 0}</span> produits</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dynamic Categories Section */}
      <div className="admin-category-categories-section">
        <div className="admin-category-section-header-admin">
          <h2>Catégories Additionnelles (Autres)</h2>
          <span className="admin-category-section-badge">{dynamicCategories.length} catégories</span>
        </div>

        {/* Search */}
        <div className="admin-category-categories-search">
          <svg className="admin-category-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher une catégorie additionnelle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="admin-category-search-input"
          />
        </div>

        {/* Dynamic Categories Grid */}
        {loading ? (
          <div className="admin-category-loading-state">
            <div className="admin-category-spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : filteredDynamicCategories.length === 0 ? (
          <div className="admin-category-empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7V4a2 2 0 0 1 2-2h8.5L20 7.5V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="15" x2="12" y2="11" />
              <line x1="10" y1="13" x2="14" y2="13" />
            </svg>
            <h3>Aucune catégorie additionnelle</h3>
            <p>Les catégories additionnelles apparaîtront dans le menu "Autres"</p>
          </div>
        ) : (
          <div className="admin-category-categories-grid">
            {filteredDynamicCategories.map(category => (
              <div key={category.id} className="admin-category-category-card">
                <div className="admin-category-category-card-header">
                  <div 
                    className="admin-category-category-icon" 
                    style={{ background: category.color || '#3b82f6' }}
                  >
                    {category.icon || getCategoryInitials(category.name)}
                  </div>
                  <div className="admin-category-category-actions">
                    <button
                      className="admin-category-cat-btn-icon admin-category-cat-btn-edit"
                      onClick={() => handleEdit(category)}
                      title="Modifier"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className={`admin-category-cat-btn-icon admin-category-cat-btn-delete ${deleteConfirm === category.id ? 'confirm' : ''}`}
                      onClick={() => handleDelete(category.id)}
                      title={deleteConfirm === category.id ? 'Cliquez pour confirmer' : 'Supprimer'}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="admin-category-category-info">
                  <h3>{category.name}</h3>
                  <p>{category.description || 'Aucune description'}</p>
                </div>
                <div className="admin-category-category-stats">
                  <div className="admin-category-stat-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    </svg>
                    <span><span className="admin-category-stat-value">{category.productCount || 0}</span> produits</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-category-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-category-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-category-modal-header">
              <h2>
                {modalMode === 'add' ? 'Ajouter une catégorie' : 'Modifier la catégorie'}
                {selectedCategory?.isFixed && <span className="admin-category-modal-badge">Principale</span>}
              </h2>
              <button className="admin-category-modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-category-modal-form">
              <div className="admin-category-form-group">
                <label>Nom de la catégorie *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: Bureaux"
                  disabled={submitting}
                />
              </div>

              <div className="admin-category-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Description de la catégorie"
                  disabled={submitting}
                />
              </div>

              <div className="admin-category-form-group">
                <label>Icône (emoji ou texte)</label>
                <input
                  type="text"
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  placeholder="Ex: 🪑 ou laissez vide pour initiales"
                  maxLength="2"
                  disabled={submitting}
                />
              </div>

              <div className="admin-category-form-group">
                <label>Couleur</label>
                <div className="admin-category-color-picker-group">
                  <div className="admin-category-color-input-wrapper">
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      placeholder="#3b82f6"
                      disabled={submitting}
                    />
                  </div>
                  <div 
                    className="admin-category-color-preview" 
                    style={{ background: formData.color }}
                    onClick={() => !submitting && document.getElementById('color-input').click()}
                  />
                  <input
                    id="color-input"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="admin-category-modal-footer">
                <button 
                  type="button" 
                  className="admin-category-cat-btn cat-btn-secondary" 
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="admin-category-cat-btn admin-category-cat-btn-primary" 
                  disabled={submitting}
                >
                  {submitting ? 'Enregistrement...' : modalMode === 'add' ? 'Ajouter' : 'Modifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;